import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  ChevronRight, 
  ArrowRight,
  AlertTriangle,
  BookOpen,
  Clock,
  Calendar,
  Target,
  Info,
  TrendingDown,
  Settings,
  Gauge,
  Wrench,
  HandshakeIcon,
  ShoppingCart,
  Truck,
  User,
  Mail,
  Phone,
  MapPin,
  Check,
  Unlock
} from 'lucide-react';

// Step indicator component - moved outside to avoid re-renders
const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {[1, 2, 3].map((step, index) => (
      <React.Fragment key={step}>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
          currentStep >= step 
            ? 'bg-[#635BFF] text-white' 
            : 'bg-gray-200 text-gray-500'
        }`}>
          {currentStep > step ? <Check className="w-5 h-5" /> : step}
        </div>
        {index < 2 && (
          <div className={`w-16 h-1 mx-2 rounded transition-all ${
            currentStep > step ? 'bg-[#635BFF]' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const EstimerForeuse = () => {
  // Configuration des coefficients
  const CFG = {
    heuresAnnuelles: 1500,
    decotePremiereAnnee: 0.18,
    decoteParAnSuivante: 0.08,
    penaliteParTrancheHeures: 0.05,
    trancheHeures: 2000,
    plancherCoefHeures: 0.55,
    etat: { excellent: 1.00, bon: 0.90, moyen: 0.75, repar: 0.55 },
    largeurFourchette: 0.12,
    facteurMarche: 0.80,
    seuilAgeResiduel: 15
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Technical
    marque: '',
    modele: '',
    serie: '',
    neuf: '',
    annee: '',
    heures: '',
    // Step 2 - Context
    etat: 'bon',
    codePostal: '',
    // Step 3 - Contact
    nom: '',
    email: '',
    telephone: ''
  });
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Sections for table of contents
  const sections = [
    { id: 'section-outil', title: "Caractéristiques machine" },
    { id: 'section-methode', title: "Comment ça fonctionne" },
    { id: 'section-limites', title: "Limites de l'estimation" },
    { id: 'section-guides', title: "Pour aller plus loin" },
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const formatEuros = (n) => {
    const rounded = Math.round(n / 100) * 100;
    return rounded.toLocaleString('fr-FR') + ' €';
  };

  const calculate = () => {
    const neuf = parseFloat(formData.neuf);
    const annee = parseInt(formData.annee);
    const heures = parseFloat(formData.heures);
    const currentYear = new Date().getFullYear();

    const age = Math.max(0, currentYear - annee);
    const seuilH = CFG.seuilAgeResiduel * CFG.heuresAnnuelles;

    // Machine en fin de cycle
    if (age >= CFG.seuilAgeResiduel || heures >= seuilH) {
      setResult({
        isResiduel: true,
        message: "Vu son âge et/ou ses heures, la valeur dépend surtout de l'état réel et des pièces récupérables. Estimation précise en visite."
      });
      return;
    }

    // Calcul normal
    let cAge = age === 0 ? 1 : (1 - CFG.decotePremiereAnnee) * Math.pow(1 - CFG.decoteParAnSuivante, age - 1);
    const attendu = Math.max(CFG.heuresAnnuelles, CFG.heuresAnnuelles * age);
    const exces = Math.max(0, heures - attendu);
    let cH = Math.max(1 - (exces / CFG.trancheHeures) * CFG.penaliteParTrancheHeures, CFG.plancherCoefHeures);
    const cEtat = CFG.etat[formData.etat];

    const centrale = neuf * cAge * cH * cEtat * CFG.facteurMarche;
    const bas = Math.round(centrale * (1 - CFG.largeurFourchette) / 100) * 100;
    const haut = Math.round(centrale * (1 + CFG.largeurFourchette) / 100) * 100;

    setResult({
      isResiduel: false,
      bas,
      haut,
      centrale: Math.round(centrale / 100) * 100,
      exces: exces > 0,
      attendu,
      details: {
        neuf,
        cAge: cAge.toFixed(2),
        cH: cH.toFixed(2),
        cEtat: cEtat.toFixed(2),
        facteurMarche: CFG.facteurMarche
      }
    });
  };

  const validateStep1 = () => {
    const newErrors = {};
    const neuf = parseFloat(formData.neuf);
    const annee = parseInt(formData.annee);
    const heures = parseFloat(formData.heures);
    const currentYear = new Date().getFullYear();

    if (!formData.marque.trim()) newErrors.marque = 'Entrez la marque';
    if (!formData.modele.trim()) newErrors.modele = 'Entrez le modèle';
    if (isNaN(neuf) || neuf <= 0) newErrors.neuf = 'Entrez un prix neuf valide';
    if (isNaN(annee) || annee < 1980 || annee > currentYear) newErrors.annee = 'Entrez une année valide';
    if (isNaN(heures) || heures < 0) newErrors.heures = 'Entrez les heures de forage';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.codePostal || formData.codePostal.length < 4) {
      newErrors.codePostal = 'Entrez un code postal valide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Entrez votre nom';
    if (!formData.email.trim() || !formData.email.includes('@')) newErrors.email = 'Entrez un email valide';
    if (!formData.telephone.trim() || formData.telephone.length < 10) newErrors.telephone = 'Entrez un téléphone valide';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      calculate();
      setCurrentStep(3);
    }
  };

  const handleSubmit = async () => {
    if (validateStep3()) {
      setIsSubmitting(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const formPayload = new FormData();
        formPayload.append('nom', formData.nom);
        formPayload.append('telephone', formData.telephone);
        formPayload.append('email', formData.email);
        formPayload.append('code_postal', formData.codePostal);
        formPayload.append('marque', formData.marque);
        formPayload.append('modele', formData.modele);
        formPayload.append('serie', formData.serie || '');
        formPayload.append('prix_neuf', formData.neuf || '0');
        formPayload.append('annee', formData.annee || '0');
        formPayload.append('heures', formData.heures || '0');
        formPayload.append('etat', formData.etat);
        if (result && !result.isResiduel) {
          formPayload.append('estimation_bas', result.bas);
          formPayload.append('estimation_haut', result.haut);
          formPayload.append('is_residuel', 'false');
        } else if (result && result.isResiduel) {
          formPayload.append('is_residuel', 'true');
        }
        formPayload.append('rgpd_accepted', 'true');

        const response = await fetch(`${API_URL}/api/demandes/estimation-foreuse`, {
          method: 'POST',
          body: formPayload
        });

        if (response.ok) {
          setSubmitSuccess(true);
          setTimeout(() => {
            document.getElementById('result-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          console.error('Erreur lors de la soumission');
        }
      } catch (error) {
        console.error('Erreur réseau:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Estimateur de valeur d'une foreuse d'occasion",
    "description": "Outil d'estimation de la valeur de revente d'une foreuse géotechnique basé sur la décote du prix neuf selon l'âge, les heures et l'état.",
    "url": "https://geots.consulting/calcul/estimer-ma-foreuse",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Comment estimer la valeur d'une foreuse d'occasion ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La valeur d'une foreuse d'occasion s'estime en appliquant une décote sur le prix neuf de référence. Cette décote dépend de l'âge de la machine, du nombre d'heures de forage, et de l'état général d'entretien."
        }
      },
      {
        "@type": "Question",
        "name": "Quelle est la décote moyenne d'une foreuse par an ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "En moyenne, une foreuse perd environ 18% de sa valeur la première année, puis 8% par an les années suivantes. L'état d'entretien et les heures d'utilisation impactent significativement cette décote."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Calculez la valeur de marché de votre foreuse d&apos;occasion | GEOTS</title>
        <meta name="description" content="Estimez gratuitement la valeur de revente de votre foreuse géotechnique. Outil basé sur la décote du prix neuf selon l'âge, les heures et l'état d'entretien." />
        <link rel="canonical" href="https://geots.consulting/calcul/estimer-ma-foreuse" />
        <meta property="og:title" content="Calculez la valeur de marché de votre foreuse d'occasion | GEOTS" />
        <meta property="og:description" content="Estimez la valeur de revente de votre foreuse d'occasion. Outil gratuit basé sur la décote selon âge, heures et état." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/calcul/estimer-ma-foreuse" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/calcul" className="hover:text-[#635BFF]">Calculs</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Estimer ma foreuse</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  OUTIL INTERACTIF
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 26 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  ~3 min d&apos;utilisation
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Calculez la valeur de marché
                <span className="text-[#00D4FF]"> de votre foreuse d&apos;occasion</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Estimation déclarative indicative basée sur la décote du prix neuf selon l&apos;âge, 
                les heures de forage et l&apos;état d&apos;entretien. Prix réel confirmé après visite.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('section-outil')}
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  <Calculator className="w-5 h-5" />
                  Estimer ma foreuse
                </button>
                <Link 
                  to="/guides/acheter-foreuse-occasion" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Guide foreuse d&apos;occasion
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Quick disclaimer in sidebar */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-start gap-2 text-amber-700 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Estimation déclarative indicative. Prix ferme après visite.</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              
              {/* Section 1 - L'outil */}
              <section id="section-outil" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                  Entrez les caractéristiques de votre machine
                </h2>

                {/* Multi-step Calculator Card */}
                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-6">
                  
                  {/* Step Indicator */}
                  <StepIndicator currentStep={currentStep} />

                  {/* Step Labels */}
                  <div className="flex justify-between text-xs text-gray-500 mb-8 px-2">
                    <span className={currentStep >= 1 ? 'text-[#635BFF] font-medium' : ''}>Votre machine</span>
                    <span className={currentStep >= 2 ? 'text-[#635BFF] font-medium' : ''}>État & localisation</span>
                    <span className={currentStep >= 3 ? 'text-[#635BFF] font-medium' : ''}>Vos coordonnées</span>
                  </div>

                  {/* Step 1: Technical Info */}
                  {currentStep === 1 && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block font-semibold text-[#0A2540] mb-2">Marque</label>
                          <input
                            type="text"
                            name="marque"
                            value={formData.marque}
                            onChange={handleChange}
                            placeholder="ex. Comacchio"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.marque ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {errors.marque && <p className="text-red-500 text-sm mt-1">{errors.marque}</p>}
                        </div>
                        <div>
                          <label className="block font-semibold text-[#0A2540] mb-2">Modèle</label>
                          <input
                            type="text"
                            name="modele"
                            value={formData.modele}
                            onChange={handleChange}
                            placeholder="ex. MC 900"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.modele ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {errors.modele && <p className="text-red-500 text-sm mt-1">{errors.modele}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">
                          N° de série
                          <span className="font-normal text-gray-500 text-sm ml-2">(facultatif)</span>
                        </label>
                        <input
                          type="text"
                          name="serie"
                          value={formData.serie}
                          onChange={handleChange}
                          placeholder="..."
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                        />
                      </div>

                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">
                          Prix neuf de référence
                          <span className="font-normal text-gray-500 text-sm ml-2">(€)</span>
                        </label>
                        <input
                          type="number"
                          name="neuf"
                          value={formData.neuf}
                          onChange={handleChange}
                          placeholder="ex. 400000"
                          min="1000"
                          step="1000"
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.neuf ? 'border-red-500' : 'border-gray-200'}`}
                        />
                        {errors.neuf && <p className="text-red-500 text-sm mt-1">{errors.neuf}</p>}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block font-semibold text-[#0A2540] mb-2">Année de mise en service</label>
                          <input
                            type="number"
                            name="annee"
                            value={formData.annee}
                            onChange={handleChange}
                            placeholder="ex. 2019"
                            min="1980"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.annee ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {errors.annee && <p className="text-red-500 text-sm mt-1">{errors.annee}</p>}
                        </div>
                        <div>
                          <label className="block font-semibold text-[#0A2540] mb-2">Heures de forage</label>
                          <input
                            type="number"
                            name="heures"
                            value={formData.heures}
                            onChange={handleChange}
                            placeholder="ex. 7000"
                            min="0"
                            step="100"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.heures ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {errors.heures && <p className="text-red-500 text-sm mt-1">{errors.heures}</p>}
                        </div>
                      </div>

                      <button
                        onClick={handleNextStep}
                        className="w-full bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        Suivant
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {/* Step 2: Context */}
                  {currentStep === 2 && (
                    <div className="space-y-5">
                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">État général et entretien</label>
                        <select
                          name="etat"
                          value={formData.etat}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                        >
                          <option value="excellent">Excellent — carnet complet, révisions à jour</option>
                          <option value="bon">Bon — entretien suivi, usure normale</option>
                          <option value="moyen">Moyen — entretien partiel, travaux à prévoir</option>
                          <option value="repar">À réviser — réparations importantes</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-semibold text-[#0A2540] mb-2">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Code postal de la machine
                        </label>
                        <input
                          type="text"
                          name="codePostal"
                          value={formData.codePostal}
                          onChange={handleChange}
                          placeholder="ex. 75001"
                          maxLength={5}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all ${errors.codePostal ? 'border-red-500' : 'border-gray-200'}`}
                        />
                        {errors.codePostal && <p className="text-red-500 text-sm mt-1">{errors.codePostal}</p>}
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setCurrentStep(1)}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                        >
                          Retour
                        </button>
                        <button
                          onClick={handleNextStep}
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                          Calculer mon estimation
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Contact Info */}
                  {currentStep === 3 && (
                    <div className="space-y-5">
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <p className="text-sm text-gray-600 mb-4">
                          <Info className="w-4 h-4 inline mr-1 text-[#635BFF]" />
                          Entrez vos coordonnées pour débloquer la valeur estimée de votre machine
                        </p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <User className="w-4 h-4 inline mr-1" />
                              Votre nom
                            </label>
                            <input
                              type="text"
                              name="nom"
                              value={formData.nom}
                              onChange={handleChange}
                              placeholder="Jean Dupont"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.nom ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
                          </div>

                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <Mail className="w-4 h-4 inline mr-1" />
                              Votre email
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="jean@exemple.fr"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                          </div>

                          <div>
                            <label className="block font-semibold text-[#0A2540] mb-2">
                              <Phone className="w-4 h-4 inline mr-1" />
                              Votre téléphone
                            </label>
                            <input
                              type="tel"
                              name="telephone"
                              value={formData.telephone}
                              onChange={handleChange}
                              placeholder="06 12 34 56 78"
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white ${errors.telephone ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {errors.telephone && <p className="text-red-500 text-sm mt-1">{errors.telephone}</p>}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setCurrentStep(2)}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                        >
                          Retour
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Unlock className="w-5 h-5" />
                              Débloquer la valeur de ma machine
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Result Section - Only shown after Step 3 validation and successful submission */}
                {result && currentStep === 3 && submitSuccess && (
                  <div id="result-box" className="bg-white rounded-2xl shadow-sm border overflow-hidden mb-6">
                    <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] p-4">
                      <h3 className="font-bold text-white text-lg">Valeur estimée de votre {formData.marque} {formData.modele}</h3>
                    </div>
                    <div className="p-6">
                      
                      {result.isResiduel ? (
                        /* Machine en fin de cycle */
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                          <div className="flex items-start gap-3">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <strong className="text-blue-700">Machine en fin de cycle</strong>
                              <p className="text-blue-700 mt-2">{result.message}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Résultat normal */
                        <>
                          {/* Usure indicator */}
                          {result.exces ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-800">
                              <Settings className="w-4 h-4 inline mr-2" />
                              Machine plus utilisée que l&apos;usage moyen attendu (~{result.attendu.toLocaleString('fr-FR')} h). Pris en compte.
                            </div>
                          ) : (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-sm text-green-800">
                              <Gauge className="w-4 h-4 inline mr-2" />
                              Heures cohérentes pour l&apos;âge (attendu ~{result.attendu.toLocaleString('fr-FR')} h).
                            </div>
                          )}

                          {/* Fourchette */}
                          <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6 text-center mb-4">
                            <div className="text-sm text-gray-500 uppercase tracking-wide mb-2">Estimation déclarative indicative</div>
                            <div className="text-3xl font-bold text-[#635BFF]">
                              {formatEuros(result.bas)} – {formatEuros(result.haut)}
                            </div>
                            <div className="text-sm text-gray-500 mt-2">Fourchette de départ, à confirmer après visite</div>
                          </div>

                          {/* Détail calcul */}
                          <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4 text-xs text-gray-500 font-mono mb-4">
                            Détail : {formatEuros(result.details.neuf)} × âge {result.details.cAge} × heures {result.details.cH} × état {result.details.cEtat} × marché {result.details.facteurMarche} = valeur centrale {formatEuros(result.centrale)}
                          </div>
                        </>
                      )}

                      {/* Options CTA */}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                          <h4 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                            <HandshakeIcon className="w-5 h-5 text-[#635BFF]" />
                            Gérer ma vente
                          </h4>
                          <p className="text-sm text-gray-600 mb-4">GEOTS vous accompagne pour vendre à un professionnel de son réseau.</p>
                          <Link
                            to="/vendre"
                            className="block w-full bg-[#635BFF] hover:bg-[#5248e5] text-white text-center font-semibold py-3 px-4 rounded-lg transition-colors"
                          >
                            Être accompagné →
                          </Link>
                        </div>
                        <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                          <h4 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                            <Truck className="w-5 h-5 text-[#0A2540]" />
                            Rachat direct
                          </h4>
                          <p className="text-sm text-gray-600 mb-4">GEOTS étudie le rachat. Un commercial vous contacte et organise la visite.</p>
                          <Link
                            to="/rachat/visite"
                            className="block w-full bg-[#0A2540] hover:bg-[#1a3a5c] text-white text-center font-semibold py-3 px-4 rounded-lg transition-colors"
                          >
                            Demander une visite →
                          </Link>
                        </div>
                      </div>

                      {/* Warning */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <strong className="text-amber-700">Estimation déclarative et indicative.</strong>
                            <p className="mt-2">
                              Basée sur vos seules déclarations, sans inspection. <strong>Prix ferme établi après visite.</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* Section 2 - Comment ça fonctionne */}
              <section id="section-methode" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                  Comment fonctionne l&apos;estimation
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 space-y-6">
                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-[#635BFF]" />
                      Principe de la décote
                    </h3>
                    <p className="text-gray-600 mb-4">
                      La valeur d&apos;une foreuse d&apos;occasion s&apos;estime en appliquant une <strong>décote sur le prix neuf de référence</strong>. 
                      Cette décote tient compte de trois facteurs principaux :
                    </p>
                    <ul className="space-y-3 text-gray-600">
                      <li className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                        <span><strong>L&apos;âge de la machine</strong> : environ 18% la première année, puis 8% par an</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Gauge className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                        <span><strong>Les heures de forage</strong> : pénalité si usage supérieur à ~1 500 h/an</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Wrench className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                        <span><strong>L&apos;état d&apos;entretien</strong> : de 100% (excellent) à 55% (à réviser)</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3">Formule simplifiée</h3>
                    <div className="bg-[#0A2540] text-white px-4 py-3 rounded-lg font-mono text-center mb-4">
                      Valeur = Prix neuf × Coef. âge × Coef. heures × Coef. état × Facteur marché
                    </div>
                    <p className="text-gray-600">
                      Le <strong>facteur marché</strong> (actuellement 0,80) reflète la réalité du marché de l&apos;occasion 
                      pour les foreuses géotechniques par rapport aux valorisations théoriques.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3">Fourchette de résultat</h3>
                    <p className="text-gray-600">
                      Une marge de ±12% est appliquée autour de la valeur centrale pour donner une <strong>fourchette réaliste</strong>. 
                      La valeur finale dépendra de l&apos;inspection physique et de la négociation.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 3 - Limites */}
              <section id="section-limites" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                  Limites de l&apos;estimation
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-amber-700">Cet outil donne une estimation indicative, pas un prix de vente garanti.</strong>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Basé sur vos déclarations</strong>
                        <p className="text-gray-600 text-sm">L&apos;estimation repose uniquement sur les informations que vous saisissez. Aucune vérification n&apos;est effectuée.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Ne tient pas compte des spécificités</strong>
                        <p className="text-gray-600 text-sm">Options, équipements supplémentaires, historique de panne, localisation géographique, urgence de vente...</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-[#0A2540]">Marché fluctuant</strong>
                        <p className="text-gray-600 text-sm">Les prix du marché de l&apos;occasion évoluent selon l&apos;offre, la demande et la conjoncture économique.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-[#0A2540] text-white rounded-xl">
                    <p className="font-semibold mb-2">Pour obtenir une valorisation précise :</p>
                    <p className="text-gray-300 text-sm">
                      Un expert GEOTS peut se déplacer pour inspecter votre machine et vous fournir une offre ferme 
                      de rachat ou une estimation de vente réaliste sur le marché.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4 - Guides connexes */}
              <section id="section-guides" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                  Pour aller plus loin
                </h2>

                <div className="space-y-4">
                  <Link 
                    to="/guides/acheter-foreuse-occasion"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-orange-500 p-3 rounded-xl">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Guide : Acheter une foreuse d&apos;occasion</p>
                          <p className="text-sm text-gray-600">Points de contrôle, pièges à éviter, négociation</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                  
                  <Link 
                    to="/guides/comment-choisir-foreuse-geotechnique"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-500 p-3 rounded-xl">
                          <Settings className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Comment choisir une foreuse géotechnique</p>
                          <p className="text-sm text-gray-600">Critères de sélection, types de machines, applications</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>

                  <Link 
                    to="/vendre"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#635BFF] p-3 rounded-xl">
                          <HandshakeIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Vendre mon matériel</p>
                          <p className="text-sm text-gray-600">Publiez votre annonce ou faites-vous accompagner</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                </div>
              </section>

              {/* Final CTA */}
              <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white text-center">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h2 className="text-2xl font-bold mb-3">Vous souhaitez vendre votre foreuse ?</h2>
                <p className="text-white/80 mb-6 max-w-xl mx-auto">
                  GEOTS vous accompagne dans la vente ou rachète directement votre matériel. 
                  Contactez-nous pour une estimation sur site.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/vendre"
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#635BFF] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                  >
                    <HandshakeIcon className="w-5 h-5" />
                    Vendre mon matériel
                  </Link>
                  <Link 
                    to="/rachat/visite"
                    className="inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1a3a5c] transition-colors"
                  >
                    <Truck className="w-5 h-5" />
                    Demander une visite
                  </Link>
                </div>
              </section>

            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default EstimerForeuse;
